import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Scale, ShieldCheck, LineChart, FileSearch, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/9ad6fcf0-f300-480e-a17c-fa8b74dce387/images/b0b2fb5b889185ec9be9c43b6bf8ca889a398a53c1add6d7eee4d3f81b799221.png";
const VITALIST_BG = "https://static.prod-images.emergentagent.com/jobs/9ad6fcf0-f300-480e-a17c-fa8b74dce387/images/4cc9af13cacd7335112e68a81b69f72e4e3d0b575a70f5f0109aa3011d3a0742.png";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-foreground/70">
            <a href="#features" className="hover:text-foreground transition">Modules</a>
            <a href="#methodology" className="hover:text-foreground transition">Méthodologie</a>
            <a href="#author" className="hover:text-foreground transition">Auteur</a>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="outline" size="sm" className="h-9" data-testid="nav-login-btn">
              <Link to="/login">Connexion</Link>
            </Button>
            <Button asChild size="sm" className="h-9 bg-marine hover:bg-marine/90 text-white" data-testid="nav-register-btn">
              <Link to="/register">Créer un compte</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden noise-overlay" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 dark:opacity-30"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" aria-hidden />
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal-up">
            <div className="inline-flex items-center gap-2 label-mono text-[10px] text-gold border border-gold/40 px-3 py-1 rounded-sm">
              <Sparkles size={12} /> Analyse · Dette · Souveraineté
            </div>
            <h1 className="mt-6 font-serif font-black tracking-tight leading-[0.95] text-5xl md:text-6xl lg:text-7xl">
              L'analyse<br />
              <span className="text-marine dark:text-gold">de la dette</span><br />
              au service du <em className="not-italic underline decoration-gold decoration-2 underline-offset-8">citoyen</em>.
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
              DEBT-ANALYZER PRO décode les conventions de prêts, rapports FMI/Banque Mondiale/BAD et lois de finances
              pour révéler le poids réel de la dette publique — en dollars, en contribuables, et en{" "}
              <span className="text-alert font-semibold">jours de vie humaine hypothéqués</span>.
            </p>
            <blockquote className="mt-8 border-l-2 border-gold pl-6 italic text-lg font-serif text-foreground/90">
              « Chaque dollar emprunté est une vie hypothéquée. »
              <div className="mt-2 label-mono text-[10px] not-italic text-muted-foreground">
                — A. ELY MUSTAPHA, Théorie Nouvelle des Finances Publiques, Bookedition
              </div>
            </blockquote>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-marine hover:bg-marine/90 text-white h-12 px-6" data-testid="hero-cta-start">
                <Link to="/register">
                  Démarrer une analyse <ArrowRight size={16} className="ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-6" data-testid="hero-cta-login">
                <Link to="/login">J'ai déjà un compte</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 reveal-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative border border-border bg-card/90 backdrop-blur p-8 rounded-sm">
              <div className="label-mono text-[10px] text-muted-foreground">FORMULE VITALISTE — V5</div>
              <h3 className="mt-2 font-serif text-2xl">Taux d'Aliénation Vitaliste</h3>
              <div className="mt-6 p-6 border border-gold/40 bg-gold/5 rounded-sm">
                <code className="block font-mono text-xl text-marine dark:text-gold">
                  V5 = (D2 / V1) × 100
                </code>
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <div><span className="label-mono text-[9px] block">D2</span>Jours de vie / contribuable</div>
                  <div><span className="label-mono text-[9px] block">V1</span>Jours de vie fiscale</div>
                  <div><span className="label-mono text-[9px] block">V5</span>TAVD %</div>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-4 gap-1 text-center">
                {[
                  { l: "< 100%", c: "bg-success", t: "Soutenable" },
                  { l: "100-200%", c: "bg-warn", t: "Tension" },
                  { l: "200-400%", c: "bg-alert", t: "Critique" },
                  { l: "> 400%", c: "bg-[#7A1F15]", t: "Insoutenable" },
                ].map((b) => (
                  <div key={b.t} className="flex flex-col">
                    <div className={`h-2 ${b.c}`} />
                    <div className="label-mono text-[9px] mt-1">{b.l}</div>
                    <div className="text-[10px] text-muted-foreground">{b.t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-border py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="label-mono text-[10px] text-gold">MODULES</div>
            <h2 className="mt-3 font-serif font-bold text-4xl md:text-5xl">
              Quatre espaces pour décoder la dette publique.
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="group border border-border p-6 rounded-sm bg-card hover-lift hover:border-gold/60 reveal-up"
                style={{ animationDelay: `${i * 0.1}s` }}
                data-testid={`feature-card-${i}`}
              >
                <f.icon className="text-marine dark:text-gold" size={28} strokeWidth={1.5} />
                <div className="mt-6 label-mono text-[9px] text-muted-foreground">{f.tag}</div>
                <h3 className="mt-2 font-serif font-bold text-xl">{f.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METHODOLOGY — vitalist */}
      <section id="methodology" className="relative border-b border-border py-20 lg:py-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${VITALIST_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/90 to-background" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="label-mono text-[10px] text-gold">THÉORIE NOUVELLE DES FINANCES PUBLIQUES</div>
            <h2 className="mt-3 font-serif font-bold text-4xl md:text-5xl leading-tight">
              Convertir la dette en<br />
              <span className="text-alert">vies humaines</span>.
            </h2>
            <p className="mt-6 text-muted-foreground text-base leading-relaxed">
              Les formules <code className="font-mono text-marine dark:text-gold">D1, D2, D4, D5</code>, le{" "}
              <code className="font-mono">TAVD (V5)</code> et la{" "}
              <code className="font-mono">charge intergénérationnelle (V6)</code> transforment les milliards d'une dette
              souveraine en une métrique que chaque citoyen comprend : <em>le nombre de jours de sa vie hypothéqués</em>.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {["D1 — Dette par contribuable actif",
                "D2 — Jours de vie hypothéqués par contribuable",
                "D4 — % de carrière aliénée à la dette",
                "V5 — Taux d'Aliénation Vitaliste (TAVD)",
                "V6 — Hypothèque intergénérationnelle"].map((x) => (
                <li key={x} className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 bg-gold shrink-0" />
                  <span><code className="font-mono text-marine dark:text-gold">{x.split(" — ")[0]}</code> — {x.split(" — ")[1]}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-gold/40 bg-card p-8 rounded-sm">
            <div className="label-mono text-[10px] text-muted-foreground">EXEMPLE ILLUSTRATIF</div>
            <h3 className="mt-2 font-serif text-2xl">L'équation de l'enfant endetté</h3>
            <div className="mt-6 p-6 border border-border bg-muted/30 rounded-sm font-mono text-sm">
              V6 = (Dette + Déficit 10 ans) / (Pop. active future × Sj futur)
            </div>
            <p className="mt-6 italic text-foreground/80">
              « Un enfant né en 2026 hérite de <span className="text-alert font-bold not-italic">1 847 jours</span> de vie
              hypothéqués dès sa naissance, sans pouvoir consentir. »
            </p>
          </div>
        </div>
      </section>

      {/* AUTHOR */}
      <section id="author" className="border-b border-border py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="label-mono text-[10px] text-gold">AUTEUR</div>
          <h2 className="mt-3 font-serif font-bold text-3xl md:text-4xl">Ahmed ELY MUSTAPHA</h2>
          <p className="mt-4 text-muted-foreground text-base">
            Juriste, Expert en Finances Publiques, PMP I-PMP IBM Full Stack Developer.
            Auteur de <em>Finances Publiques. Une Théorie Nouvelle</em> (Bookedition, Paris).
          </p>
          <div className="mt-10 flex justify-center">
            <Button asChild size="lg" className="bg-marine hover:bg-marine/90 text-white h-12 px-8" data-testid="author-cta-start">
              <Link to="/register">Accéder à l'outil d'analyse</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-xs text-muted-foreground text-center">
            © DEBT-ANALYZER PRO · Document pédagogique — n'a pas de valeur juridictionnelle.
          </p>
        </div>
      </footer>
    </div>
  );
}

const FEATURES = [
  {
    icon: BookOpen, tag: "ESPACE 1", title: "Bibliothèque documentaire",
    desc: "Import PDF, DOCX, XLSX, CSV. Classification automatique (FMI, BM, BAD, BADEA, conventions, budgets). Extraction structurée par GPT-4o.",
  },
  {
    icon: LineChart, tag: "ESPACE 2", title: "Moteur d'analyse",
    desc: "Six modules: macro-financier, vitaliste, conventions de prêts, impact social ODD, juridique, requête libre Q&A.",
  },
  {
    icon: FileSearch, tag: "ESPACE 3", title: "Visualisations",
    desc: "12 graphiques interactifs — composition de la dette, trajectoire dette/PIB, murs de refinancement, scatter conditionnalités/ODD.",
  },
  {
    icon: ShieldCheck, tag: "ESPACE 4", title: "Rapports exportables",
    desc: "Cinq presets: parlementaire, technique, citoyen, vitaliste, convention. Export PDF/Word/Excel de qualité institutionnelle.",
  },
];
